<template>
    <div
        class="relative flex items-stretch bg-white bg-opacity-0 | focus-within:bg-opacity-100"
        :class="[
            positioningClassName,
            {
                'w-full -ml-2.5 mr-2.5': animated && textAlign !== 'right',
                'w-full': animated && textAlign === 'right',
                'grow-0 shrink-0': !animated,
            },
        ]"
    >
        <component
            :id="computedId"
            :is="inputComponent"
            :aria-describedby="ariaDescribedby"
            :aria-label="ariaLabel"
            class="grow items-center"
            :class="[
                positioningClassName,
                inputExtraClassName,
                { '-ml-px': animated && textAlign !== 'right' },
            ]"
            :input-class-name="['truncate', { 'text-right': textAlign === 'right' }]"
            padding-class-name="px-2.5 py-1.5"
            autocomplete="off"
            :placeholder="placeholder"
            :type="type"
            :before="before"
            :after="after"
            :error="error || !!errorMessage"
            :error-message="errorMessage"
            :readonly="readonly"
            :disabled="disabled"
            :invisible="invisible"
            :mask="mask"
            :maxlength="maxlength"
            :allow-negative-value="allowNegativeValue"
            :model-value="internalValue"
            ref="input"
            @blur="onBlur"
            @click="$emit('click')"
            @focus="$emit('focus')"
            @keydown.enter="onEnter"
            @keydown.esc="onEsc"
            @update:model-value="syncInternalValue($event)"
        >
            <template #before>
                <slot name="before-input" />
            </template>

            <template #after>
                <activix-tooltip :content="buttonIconTooltip" v-if="buttonIconName">
                    <input-button-icon
                        data-testid="input-button-icon"
                        class="border-l"
                        :disabled="buttonIconDisabled"
                        :icon-name="buttonIconName"
                        :icon-class-name="buttonIconClassName"
                        @click="$emit('button-click')"
                    />
                </activix-tooltip>
                <slot name="after-input" v-else />
            </template>
        </component>

        <slot name="after" />
    </div>
</template>

<script>
    import { ActivixInput } from '@/components/activix-ui/index.ts';
    import { uniqueId } from 'lodash-es';

    import InputButtonIcon from '@/components/presentational/InputButtonIcon.vue';
    import DecimalInput from '@/components/container/input/DecimalInput.vue';

    /**
     * Normalizes a value for comparison by treating '', null, undefined, and NaN as equivalent.
     *
     * LiveEditInput normalizes values asymmetrically:
     * - Inbound (syncInternalValue): null -> ''  (for display)
     * - Outbound (returnValue):      '' -> null  (for API compatibility)
     *
     * Without this normalization, the update() guard compares raw '' against
     * normalized null, causing spurious emits on blur without user edits.
     * Currency fields with no saved value receive NaN (from undefined / 100),
     * which also needs normalization to prevent phantom saves.
     *
     * See: ASPD-28186, ASPD-29744
     */
    /** @param {string | number | null | undefined} value */
    function normalizeForComparison(value) {
        if (value === '' || value == null || Number.isNaN(value)) {
            return null;
        }

        return value;
    }

    export default {
        compatConfig: { MODE: 3 },

        components: {
            ActivixInput,
            InputButtonIcon,
            DecimalInput,
        },

        emits: ['click', 'focus', 'button-click', 'blur', 'update:modelValue'],

        props: {
            allowNegativeValue: {
                type: Boolean,
                default: false,
            },
            after: {
                default: '',
                type: String,
            },
            animated: {
                type: Boolean,
                default: true,
            },
            ariaDescribedby: {
                default: '',
                type: String,
            },
            ariaLabel: {
                default: '',
                type: String,
            },
            before: {
                default: '',
                type: String,
            },
            buttonIconDisabled: {
                type: Boolean,
                default: false,
            },
            buttonIconName: {
                default: '',
                type: String,
            },
            buttonIconClassName: {
                default: '',
                type: String,
            },
            buttonIconTooltip: {
                default: '',
                type: String,
            },
            disabled: {
                default: false,
                type: Boolean,
            },
            error: {
                default: false,
                type: Boolean,
            },
            errorMessage: {
                default: '',
                type: String,
            },
            id: {
                default: '',
                type: String,
            },
            invisible: {
                default: true,
                type: Boolean,
            },
            mask: {
                default: '',
                type: String,
            },
            maxlength: {
                default: null,
                type: Number,
            },
            placeholder: {
                default: '---',
                type: String,
            },
            readonly: {
                default: false,
                type: Boolean,
            },
            textAlign: {
                default: 'left',
                type: String,
                validator: (value) => ['left', 'right'].includes(value),
            },
            type: {
                default: 'text',
                type: String,
            },
            modelValue: {
                default: '',
                type: [String, Number, null],
            },
            inputExtraClassName: {
                default: '',
                type: String,
            },
        },

        data: () => ({
            cancelling: false,
            flushedValue: '',
            internalValue: '',
        }),

        computed: {
            computedId() {
                return this.id || uniqueId('input-');
            },

            inputComponent() {
                return this.usesDecimalInput ? DecimalInput : ActivixInput;
            },

            positioningClassName() {
                return [
                    'transition-all duration-150 border-gray-400',
                    { 'focus-within:ml-0': !this.disabled },
                ];
            },

            returnValue() {
                return this.internalValue === '' ? null : this.internalValue;
            },

            usesDecimalInput() {
                return this.mask === 'decimal';
            },
        },

        watch: {
            modelValue: {
                immediate: true,
                handler(newValue) {
                    this.flushedValue = newValue;

                    if (newValue === this.internalValue) {
                        return;
                    }

                    this.syncInternalValue(newValue);
                },
            },
        },

        methods: {
            blur() {
                this.$refs.input.blur();
            },

            focus() {
                this.$refs.input.focus();
            },

            onBlur() {
                this.$emit('blur');

                this.update();
            },

            onEnter() {
                this.$refs.input.blur();
            },

            onEsc() {
                this.cancelling = true;
                this.$refs.input.blur();
            },

            syncInternalValue(value) {
                this.internalValue = value === null ? '' : value;
            },

            update() {
                if (this.cancelling) {
                    this.syncInternalValue(this.modelValue);
                    this.flushedValue = this.modelValue;
                    this.cancelling = false;
                    return;
                }

                if (
                    this.readonly ||
                    normalizeForComparison(this.flushedValue) ===
                        normalizeForComparison(this.returnValue)
                ) {
                    return;
                }

                this.flushedValue = this.returnValue;
                this.$emit('update:modelValue', this.returnValue);
            },
        },

        beforeUnmount() {
            this.update();
        },
    };
</script>
